import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, Circle, Rocket, Download, Upload, FileSpreadsheet, ArrowRight } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function StepCard({ s, n }) {
  return (
    <div className={`border p-3 flex gap-3 items-start ${s.done ? "bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300" : "bg-card"}`} data-testid={`onb-step-${s.id}`}>
      <div className="flex-shrink-0 mt-0.5">{s.done ? <CheckCircle2 size={20} className="text-emerald-600" /> : <Circle size={20} className="text-muted-foreground" />}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-black text-muted-foreground tabular-nums">{String(n).padStart(2, "0")}</span>
          <span className="font-bold text-sm">{s.title}</span>
          {s.count != null && <span className="text-[10px] font-mono px-1.5 border bg-background" data-testid={`onb-count-${s.id}`}>{s.count}</span>}
          <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 ${s.done ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>{s.done ? "Listo" : "Pendiente"}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
      </div>
      <Link to={s.route} className="flex-shrink-0 text-[11px] font-bold text-primary hover:underline inline-flex items-center gap-1" data-testid={`onb-cta-${s.id}`}>{s.cta} <ArrowRight size={11} /></Link>
    </div>
  );
}

function ImportCard({ tpl, highlight }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const download = async () => {
    try {
      const r = await api.get(`/onboarding/templates/${tpl.kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = tpl.filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(tpl.endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const errs = (data.errors || []).length;
      toast[errs ? "warning" : "success"](`${tpl.title}: ${data.created ?? 0} creados${data.updated != null ? `, ${data.updated} actualizados` : ""}${data.skipped != null ? `, ${data.skipped} omitidos` : ""}${errs ? `, ${errs} errores` : ""}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className={`border p-4 bg-card ${highlight ? "ring-2 ring-primary" : ""}`} data-testid={`imp-card-${tpl.kind}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2"><FileSpreadsheet size={16} className="text-emerald-700" /><div className="font-bold text-sm">{tpl.title}</div></div>
      </div>
      <div className="text-[10px] font-mono text-muted-foreground break-words mb-2">{tpl.headers.join(" · ")}</div>
      <ul className="text-[11px] text-muted-foreground space-y-0.5 mb-3">{tpl.notes.map((n, i) => <li key={i}>• {n}</li>)}</ul>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="rounded-none text-xs" onClick={download} data-testid={`imp-download-${tpl.kind}`}><Download size={12} className="mr-1" /> Plantilla Excel</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`imp-file-${tpl.kind}`} />
        <Button size="sm" className="rounded-none text-xs" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`imp-upload-${tpl.kind}`}><Upload size={12} className="mr-1" /> {busy ? "Importando…" : "Subir archivo"}</Button>
      </div>
      {result && (
        <div className="mt-3 text-[11px] border bg-muted/30 p-2" data-testid={`imp-result-${tpl.kind}`}>
          <b>Creados {result.created ?? 0}</b>{result.updated != null && <> · Actualizados {result.updated}</>}{result.skipped != null && <> · Omitidos {result.skipped}</>} · Errores {(result.errors || []).length}
          {(result.errors || []).slice(0, 8).map((e, i) => <div key={i} className="text-destructive">{e}</div>)}
          {(result.errors || []).length > 8 && <div className="text-muted-foreground">… y {(result.errors || []).length - 8} más</div>}
        </div>
      )}
    </div>
  );
}

export default function Onboarding() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const tab = params.get("tab") || "checklist";
  const kind = params.get("kind") || "";

  const load = () => {
    api.get("/onboarding/status").then((r) => setStatus(r.data)).catch(() => setStatus(false));
    api.get("/onboarding/templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(load, []);

  return (
    <div data-testid="onboarding-page">
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1 flex items-center gap-1"><Rocket size={12} /> Puesta en marcha</div>
        <h1 className="text-3xl font-black tracking-tighter">Arranque e Importación</h1>
        <p className="text-xs text-muted-foreground mt-1 max-w-3xl">Sigue la lista en orden: con estos pasos el sistema queda listo para operar. Cada paso se marca solo cuando detecta datos.</p>
      </div>
      {status && (
        <div className="border bg-card p-4 mb-5" data-testid="onb-progress">
          <div className="flex justify-between text-xs mb-1"><span className="font-bold">Avance de arranque</span><span className="tabular-nums font-black" data-testid="onb-progress-text">{status.done}/{status.total} · {status.progress}%</span></div>
          <div className="h-3 bg-muted"><div className="h-3 bg-emerald-600 transition-all" style={{ width: `${status.progress}%` }} /></div>
        </div>
      )}
      <Tabs value={tab} onValueChange={(v) => setParams(v === "checklist" ? {} : { tab: v })}>
        <TabsList className="rounded-none">
          <TabsTrigger value="checklist" data-testid="onb-tab-checklist">Lista de verificación</TabsTrigger>
          <TabsTrigger value="import" data-testid="onb-tab-import">Centro de importación</TabsTrigger>
        </TabsList>
        <TabsContent value="checklist">
          {status === false && <p className="text-xs text-muted-foreground">Sin permiso para ver el avance.</p>}
          <div className="space-y-2">{status && status.steps.map((s, i) => <StepCard key={s.id} s={s} n={i + 1} />)}</div>
        </TabsContent>
        <TabsContent value="import">
          <p className="text-xs text-muted-foreground mb-3">Descarga la plantilla (hoja <b>Datos</b> con ejemplos + hoja <b>Instrucciones</b>), llénala y súbela aquí mismo. Orden recomendado: Clasificaciones → Artículos → Proveedores → Equipos → Personal. Acepta .xlsx y .csv.</p>
          <div className="grid md:grid-cols-2 gap-3">{templates.map((t) => <ImportCard key={t.kind} tpl={t} highlight={kind === t.kind} />)}</div>
          <div className="mt-3"><Button size="sm" variant="outline" className="rounded-none text-xs" onClick={load} data-testid="onb-refresh">Actualizar avance</Button></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
